import type { TooltipAlign, TooltipSide } from './computeTooltipCoords'
import type { TooltipContentProps, TooltipContextValue, TooltipPosition, TooltipRootProps, TooltipTriggerProps } from './Tooltip.types'
import cn from 'classnames'
import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { getReactElementRef, mergeRefs } from '../../utils'
import { useControllableState } from '../../utils/useControllableState'
import { IconButton } from '../icon-button'
import { Typography } from '../typography'
import styles from './Tooltip.module.css'
import { useTooltipPosition } from './useTooltipPosition'

const POSITION_MAP: Record<TooltipPosition, { side: TooltipSide, align: TooltipAlign }> = {
  'top-start': { side: 'top', align: 'start' },
  'top-center': { side: 'top', align: 'center' },
  'top-end': { side: 'top', align: 'end' },
  'right-start': { side: 'right', align: 'start' },
  'right-center': { side: 'right', align: 'center' },
  'right-end': { side: 'right', align: 'end' },
  'bottom-start': { side: 'bottom', align: 'start' },
  'bottom-center': { side: 'bottom', align: 'center' },
  'bottom-end': { side: 'bottom', align: 'end' },
  'left-start': { side: 'left', align: 'start' },
  'left-center': { side: 'left', align: 'center' },
  'left-end': { side: 'left', align: 'end' },
}

type TooltipContentCSSProperties = React.CSSProperties & {
  '--_tooltip-top'?: string
  '--_tooltip-left'?: string
}

const TooltipContext = React.createContext<TooltipContextValue | null>(null)

function useTooltipContext(component: string): TooltipContextValue {
  const context = React.use(TooltipContext)

  if (!context) {
    throw new Error(`Tooltip.${component} must be used within a Tooltip.Root`)
  }

  return context
}

function composeEventHandlers<E extends React.SyntheticEvent>(
  originalHandler: ((event: E) => void) | undefined,
  ourHandler: (event: E) => void,
) {
  return (event: E) => {
    originalHandler?.(event)

    if (!event.defaultPrevented) {
      ourHandler(event)
    }
  }
}

function Root({ open: openProp, defaultOpen = false, onOpenChange, children }: TooltipRootProps) {
  const [open, setUncontrolledOpen] = useControllableState(openProp, defaultOpen)
  const [isPersistent, setIsPersistent] = React.useState(false)
  const triggerRef = React.useRef<HTMLElement>(null)
  const suppressFocusOpenRef = React.useRef(false)
  const tooltipId = React.useId()

  const setOpen = React.useCallback((next: boolean) => {
    setUncontrolledOpen(next)
    onOpenChange?.(next)
  }, [setUncontrolledOpen, onOpenChange])

  const closeAndRestoreFocus = React.useCallback(() => {
    suppressFocusOpenRef.current = true
    setOpen(false)
    triggerRef.current?.focus()
  }, [setOpen])

  const consumeFocusSuppression = React.useCallback(() => {
    if (suppressFocusOpenRef.current) {
      suppressFocusOpenRef.current = false
      return true
    }

    return false
  }, [])

  const value = React.useMemo<TooltipContextValue>(() => ({
    open,
    setOpen,
    triggerRef,
    tooltipId,
    isPersistent,
    setIsPersistent,
    closeAndRestoreFocus,
    consumeFocusSuppression,
  }), [open, setOpen, tooltipId, isPersistent, closeAndRestoreFocus, consumeFocusSuppression])

  return <TooltipContext value={value}>{children}</TooltipContext>
}

Root.displayName = 'Tooltip.Root'

type TriggerElement = React.ReactElement<{
  onMouseEnter?: React.MouseEventHandler
  onMouseLeave?: React.MouseEventHandler
  onFocus?: React.FocusEventHandler
  onBlur?: React.FocusEventHandler
  ref?: React.Ref<HTMLElement>
}>

function Trigger({ children }: TooltipTriggerProps) {
  const { open, setOpen, triggerRef, tooltipId, isPersistent, consumeFocusSuppression } = useTooltipContext('Trigger')

  if (!React.isValidElement(children)) {
    throw new Error('Tooltip.Trigger expects a single React element as its child.')
  }

  const child = children as TriggerElement

  const handleOpen = React.useCallback(() => {
    if (consumeFocusSuppression()) {
      return
    }

    setOpen(true)
  }, [setOpen, consumeFocusSuppression])

  const handleClose = React.useCallback(() => {
    if (!isPersistent) {
      setOpen(false)
    }
  }, [isPersistent, setOpen])

  // eslint-disable-next-line react/no-clone-element
  return React.cloneElement<React.HTMLAttributes<HTMLElement> & { ref?: React.Ref<HTMLElement> }>(child, {
    'ref': mergeRefs(triggerRef, getReactElementRef<HTMLElement>(child) ?? null),
    'aria-describedby': open ? tooltipId : undefined,
    'onMouseEnter': composeEventHandlers(child.props.onMouseEnter, handleOpen),
    'onMouseLeave': composeEventHandlers(child.props.onMouseLeave, handleClose),
    'onFocus': composeEventHandlers(child.props.onFocus, handleOpen),
    'onBlur': composeEventHandlers(child.props.onBlur, handleClose),
  })
}

Trigger.displayName = 'Tooltip.Trigger'

function Content({
  position = 'top-center',
  hasArrow = true,
  hasDismissButton = false,
  dismissButtonLabel = 'Close',
  text,
  supportingText,
  className,
}: TooltipContentProps) {
  const { open, triggerRef, tooltipId, setIsPersistent, closeAndRestoreFocus } = useTooltipContext('Content')
  const contentRef = React.useRef<HTMLDivElement>(null)
  const { side, align } = POSITION_MAP[position]
  const coords = useTooltipPosition(open, side, align, triggerRef, contentRef)

  React.useEffect(() => {
    setIsPersistent(hasDismissButton)
  }, [hasDismissButton, setIsPersistent])

  React.useEffect(() => {
    if (!open) {
      return
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeAndRestoreFocus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, closeAndRestoreFocus])

  if (!open || typeof document === 'undefined') {
    return null
  }

  const handleDismissClick = () => {
    closeAndRestoreFocus()
  }

  const contentStyles: TooltipContentCSSProperties = {
    '--_tooltip-top': `${coords?.top ?? -9999}px`,
    '--_tooltip-left': `${coords?.left ?? -9999}px`,
  }

  return ReactDOM.createPortal(
    <div
      ref={contentRef}
      id={tooltipId}
      role="tooltip"
      data-side={side}
      data-align={align}
      data-arrow={hasArrow}
      className={cn(styles.content, !coords && styles.contentPending, className)}
      style={contentStyles}
    >
      <div className={styles.textContainer}>
        <Typography variant="body-s-bold-tight" className={styles.text}>
          {text}
        </Typography>
        {supportingText && (
          <Typography variant="body-s-regular-tight" className={styles.supportingText}>
            {supportingText}
          </Typography>
        )}
      </div>
      {hasDismissButton && (
        <IconButton
          variant="transparent-neutral"
          size="XS"
          glyph="DismissSmall"
          aria-label={dismissButtonLabel}
          onClick={handleDismissClick}
          className={styles.dismissButton}
        />
      )}
    </div>,
    document.body,
  )
}

Content.displayName = 'Tooltip.Content'

export const Tooltip = {
  Root,
  Trigger,
  Content,
}
